import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../utils/locale';

const SubscriptionWarning: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check subscription info from localStorage
    const storedSubscription = localStorage.getItem('subscription');
    if (storedSubscription) {
      const subInfo = JSON.parse(storedSubscription);
      setSubscription(subInfo);
      
      // Show warning if subscription is expired or expiring soon
      if (subInfo.expired || subInfo.showWarning) {
        setOpen(true);
      }
      
      // Clear subscription info after showing
      setTimeout(() => {
        localStorage.removeItem('subscription');
      }, 1000);
    }
  }, [location.pathname]);

  const handleClose = () => {
    setOpen(false);
    
    // If subscription is expired and user is not admin, limit access
    if (subscription?.expired && user?.role !== 'admin') {
      // Allow access only to profile and settings pages
      const allowedPaths = ['/profile', '/settings', '/dashboard'];
      if (!allowedPaths.includes(location.pathname)) {
        navigate('/dashboard');
      }
    }
  };

  const handleContactAdmin = () => {
    navigate('/settings');
    setOpen(false);
  };

  if (!subscription) return null;

  const getProgressColor = () => {
    if (subscription.daysRemaining <= 3) return 'error';
    if (subscription.daysRemaining <= 7) return 'warning';
    return 'primary';
  };

  const getProgressValue = () => {
    if (subscription.expired) return 0;
    if (!subscription.daysRemaining) return 0;
    // Calculate percentage based on 30 days trial
    return Math.max(0, Math.min(100, (subscription.daysRemaining / 30) * 100));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={subscription?.expired}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {subscription?.expired ? (
            <>
              <ErrorIcon color="error" />
              <Typography variant="h6">{t('Üyelik Süreniz Doldu')}</Typography>
            </>
          ) : (
            <>
              <WarningIcon color="warning" />
              <Typography variant="h6">{t('Üyelik Süresi Uyarısı')}</Typography>
            </>
          )}
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        {subscription?.expired ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {t('Üyelik süreniz dolmuştur!')}
            </Typography>
            <Typography variant="body2">
              {t('Sistemi kullanmaya devam etmek için lütfen sistem yöneticisi ile iletişime geçiniz.')}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {t('Üyeliğinizin bitmesine {{days}} gün kaldı!', { days: subscription?.daysRemaining, count: subscription?.daysRemaining })}
            </Typography>
            <Typography variant="body2">
              {t('Kesintisiz hizmet için lütfen üyeliğinizi yenileyin.')}
            </Typography>
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Abonelik Durumu')}
            </Typography>
            <Chip 
              label={subscription?.type?.toUpperCase() || t('DENEME')} 
              size="small"
              color={subscription?.expired ? 'error' : 'primary'}
            />
          </Stack>
          
          {!subscription?.expired && (
            <>
              <LinearProgress 
                variant="determinate" 
                value={getProgressValue()} 
                color={getProgressColor()}
                sx={{ height: 8, borderRadius: 1, mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t('Bitiş Tarihi:')} {subscription?.endDate ? 
                  new Date(subscription.endDate).toLocaleDateString(getLocale()) : 
                  t('Belirtilmemiş')
                }
              </Typography>
            </>
          )}
        </Box>
        
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            {t('Abonelik Bilgileri')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {t('Abonelik Tipi:')} {subscription?.type || t('Deneme')}
            <br />
            • {t('Durum:')} {subscription?.status === 'active' ? t('Aktif') : t('Pasif')}
            {subscription?.isTrial && (
              <>
                <br />
                {t('• Bu bir deneme sürümüdür')}
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!subscription?.expired && (
          <Button onClick={handleClose} color="inherit">
            {t('Daha Sonra Hatırlat')}
          </Button>
        )}
        <Button 
          variant="contained" 
          onClick={handleContactAdmin}
          color={subscription?.expired ? 'error' : 'primary'}
        >
          {subscription?.expired ? t('Yönetici ile İletişime Geç') : t('Üyeliği Yenile')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionWarning;